<template>
  <div class="waybill-dashboard">
    <!-- 查询表单 -->
    <el-card class="query-card">
      <el-form :inline="true" :model="queryForm" class="query-form">
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="queryForm.dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始日期时间"
            end-placeholder="结束日期时间"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            :shortcuts="dateShortcuts"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery" :loading="loading">
            查询
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Summary 卡片 -->
    <el-row :gutter="16" class="summary-cards" v-if="summaryData.total > 0">
      <el-col :span="4">
        <el-card class="summary-card">
          <div class="card-title">运单总数</div>
          <div class="card-value">{{ summaryData.total }}</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="summary-card">
          <div class="card-title">揽收率</div>
          <div class="card-value">{{ summaryData.collection_rate }}%</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="summary-card">
          <div class="card-title">签收率</div>
          <div class="card-value">{{ summaryData.sign_rate }}%</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="summary-card">
          <div class="card-title">揽-运时长</div>
          <div class="card-value">{{ summaryData.collect_to_transport_hours }}h</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="summary-card">
          <div class="card-title">揽-派时长</div>
          <div class="card-value">{{ summaryData.collect_to_deliver_hours }}h</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="summary-card">
          <div class="card-title">揽-签时长</div>
          <div class="card-value">{{ summaryData.collect_to_sign_hours }}h</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表区域 -->
    <el-row :gutter="16" class="chart-row" v-if="resList.length > 0">
      <!-- 趋势折线图 -->
      <el-col :span="14">
        <el-card>
          <template #header>
            <span>每日数据趋势</span>
          </template>
          <div ref="lineChartRef" class="chart-container"></div>
        </el-card>
      </el-col>
      <!-- 状态占比饼图 -->
      <el-col :span="10">
        <el-card>
          <template #header>
            <span>整体状态占比</span>
          </template>
          <div ref="pieChartRef" class="chart-container"></div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 每日明细表格 -->
    <el-card class="table-card" v-if="resList.length > 0">
      <template #header>
        <span>每日明细</span>
      </template>
      <el-table :data="resList" border stripe>
        <el-table-column prop="date" label="日期" width="120" />
        <el-table-column prop="data.total" label="运单总数" width="100" />
        <el-table-column prop="data.collection_rate" label="揽收率(%)" width="100" />
        <el-table-column prop="data.sign_rate" label="签收率(%)" width="100" />
        <el-table-column prop="data.collect_to_transport_hours" label="揽-运(h)" width="100" />
        <el-table-column prop="data.collect_to_deliver_hours" label="揽-派(h)" width="100" />
        <el-table-column prop="data.collect_to_sign_hours" label="揽-签(h)" width="100" />
        <el-table-column label="状态分布">
          <template #default="{ row }">
            <el-tag
              v-for="item in row.data.waybillStatusList"
              :key="item.statusCode"
              size="small"
              class="status-tag"
            >
              {{ getStatusName(item.statusCode) }}: {{ item.total }}({{ item.proportion }}%)
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'

// 状态码 -> 中文名映射
const STATUS_NAME_MAP = {
  '': '未发出',
  'GOT': '业务员取件',
  'COLLECT': '已揽收',
  'TRANSPORT': '运输中',
  'DELIVERING': '派送中',
  'AGENT': '入库入柜',
  'AGENT_DELIVERING': '驿站代派',
  'FAILED': '签收失败',
  'REJECT': '拒绝签收',
  'SIGN': '已签收',
  'FORWARDING': '转寄中',
  'RETURN': '退回中',
  'INVALID': '其他'
}

export default {
  name: 'WaybillDashboard',
  data() {
    return {
      queryForm: {
        dateRange: []
      },
      loading: false,
      resList: [],
      summaryData: {},
      lineChart: null,
      pieChart: null,
      statusNameMap: STATUS_NAME_MAP,
      // 快捷选项
      dateShortcuts: [
        {
          text: '最近七天',
          value: () => {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
            return [this.formatDateTime(start), this.formatDateTime(end)]
          }
        },
        {
          text: '最近三天',
          value: () => {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 3)
            return [this.formatDateTime(start), this.formatDateTime(end)]
          }
        },
        {
          text: '今天',
          value: () => {
            const now = new Date()
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            return [this.formatDateTime(start), this.formatDateTime(now)]
          }
        }
      ]
    }
  },
  mounted() {
    // 默认近七天
    const end = new Date()
    const start = new Date()
    start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
    this.queryForm.dateRange = [this.formatDateTime(start), this.formatDateTime(end)]
    this.handleQuery()
  },
  beforeUnmount() {
    if (this.lineChart) this.lineChart.dispose()
    if (this.pieChart) this.pieChart.dispose()
  },
  methods: {
    formatDateTime(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d} 00:00:00`
    },
    getStatusName(code) {
      return this.statusNameMap[code] || code || '未知'
    },
    handleQuery() {
      if (!this.queryForm.dateRange || this.queryForm.dateRange.length !== 2) {
        this.$message.warning('请选择时间范围')
        return
      }
      this.loading = true

      this.apiService.post({
        modules: 'getLogisticsData',
        content: {
          startDate: this.queryForm.dateRange[0],
          endDate: this.queryForm.dateRange[1]
        }
      })
        .then((res) => {
          if (res.status) {
            this.resList = res.data.res || []
            this.summaryData = res.data.summary || {}

            this.$nextTick(() => {
              this.initLineChart()
              this.initPieChart()
            })
          } else {
            this.$message.error(res.message || '查询失败')
          }
        })
        .catch((err) => {
          console.error('查询异常:', err)
          this.$message.error('查询异常，请稍后重试')
        })
        .finally(() => {
          this.loading = false
        })
    },
    initLineChart() {
      if (!this.$refs.lineChartRef) return
      if (this.lineChart) this.lineChart.dispose()
      this.lineChart = echarts.init(this.$refs.lineChartRef)

      const dates = this.resList.map(item => item.date)
      const totals = this.resList.map(item => item.data.total)
      const collectionRates = this.resList.map(item => item.data.collection_rate)
      const signRates = this.resList.map(item => item.data.sign_rate)
      const ctHours = this.resList.map(item => item.data.collect_to_transport_hours)
      const cdHours = this.resList.map(item => item.data.collect_to_deliver_hours)
      const csHours = this.resList.map(item => item.data.collect_to_sign_hours)

      const option = {
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['运单总数', '揽收率', '签收率', '揽-运时长', '揽-派时长', '揽-签时长'],
          bottom: 0
        },
        grid: { left: '3%', right: '4%', bottom: '10%', top: '10%', containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates
        },
        yAxis: [
          {
            type: 'value',
            name: '数量/时长'
          },
          {
            type: 'value',
            name: '比率(%)',
            max: 100
          }
        ],
        series: [
          { name: '运单总数', type: 'line', data: totals, smooth: true },
          { name: '揽收率', type: 'line', yAxisIndex: 1, data: collectionRates, smooth: true },
          { name: '签收率', type: 'line', yAxisIndex: 1, data: signRates, smooth: true },
          { name: '揽-运时长', type: 'line', data: ctHours, smooth: true },
          { name: '揽-派时长', type: 'line', data: cdHours, smooth: true },
          { name: '揽-签时长', type: 'line', data: csHours, smooth: true }
        ]
      }
      this.lineChart.setOption(option)
    },
    initPieChart() {
      if (!this.$refs.pieChartRef) return
      if (this.pieChart) this.pieChart.dispose()
      this.pieChart = echarts.init(this.$refs.pieChartRef)

      const statusList = this.summaryData.waybillStatusList || []

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          show: false
        },
        series: [
          {
            type: 'pie',
            radius: ['20%', '45%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: true,
            minAngle: 5,
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              position: 'outside',
              formatter: function (params) {
                const name = params.name
                const value = params.value
                const percent = params.percent + '%'
                return `${name}\n${value}  ${percent}`
              },
              fontSize: 12,
              lineHeight: 16,
              color: '#333'
            },
            labelLine: {
              show: true,
              length: 15,
              length2: 25,
              smooth: false
            },
            data: statusList.map(item => ({
              name: this.getStatusName(item.statusCode),
              value: item.total
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.3)'
              }
            }
          }
        ]
      }
      this.pieChart.setOption(option)
    }
  }
}
</script>

<style scoped>
.waybill-dashboard {
  padding: 16px;
}
.query-card {
  margin-bottom: 16px;
}
.query-form {
  display: flex;
  align-items: center;
}
.summary-cards {
  margin-bottom: 16px;
}
.summary-card {
  text-align: center;
}
.card-title {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}
.card-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
}
.chart-row {
  margin-bottom: 16px;
}
.chart-container {
  height: 450px;
}
.table-card {
  margin-top: 16px;
}
.status-tag {
  margin-right: 4px;
  margin-bottom: 4px;
}
</style>